import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Text,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CategoryForm from "./CategoryForm";
import WastewaterGrid from "./WastewaterGrid";
import {
  organizationQuestionnaireKeys,
  useAddWasteGeneratedInOperationsItems,
  useDeleteWasteGeneratedInOperationsItem,
  useDownloadWasteGeneratedInOperationsTemplate,
  useGetWasteGeneratedInOperationsItems,
  useImportWasteGeneratedInOperationsItems,
  useUpdateWasteGeneratedInOperationsItem,
  wasteGeneratedInOperationsKeys
} from "../../../../../../hooks/query";
import { showErrorToast, showSuccessToast } from "../../../../../../utils";
import * as React from "react";
import { useCallback, useState } from "react";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { useUpdateWasteGeneratedInOperationsCategory } from "../../../../../../hooks/query/scope-3";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { CategoryGrid } from "../CategoryGrid";
import { DeleteDialog } from "../../../../../DataEntryPlanning/components/DeleteDialog";
import { WasteGeneratedInOperationsModalWrapper } from "../CategoryModalWrappers";
import { buildDimensionFormDefaults, withDimensionItems } from "./helpers";

function WasteGeneratedInOperations({
                                      defaultValues,
                                      handleCategoryChange,
                                      tabKey,
                                      finalize,
                                      finalizeQuestionnaire,
                                      isPreview,
                                      organizationStructure,
                                      dimensionItems
                                    }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const {
    id: questionnaireId
  } = defaultValues;
  const { t, i18n } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose
  } = useDisclosure();

  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState(null);
  const [categoryModalData, setCategoryModalData] = useState(null);

  const colHeaders = [
    t(`inputs.${tabKey}.wasteActivity.name`),
    t(`inputs.${tabKey}.wasteType.name`),
    t(`inputs.${tabKey}.disposalMethod.name`),
    t(`inputs.${tabKey}.weight`)
  ];
  const attributesOrder = ["wasteActivity", "wasteType", "disposalMethod", "weight"];
  const FORM_INPUT_KEY = "wasteGeneratedInOperations";

  const {
    handleSubmit,
    formState: { isDirty }
  } = useForm();

  const queryConfig = {
    onSuccess: () => {
      showSuccessToast(toast, "Category Waste Generated In Operations saved!");
      if (finalize) {
        finalizeQuestionnaire(questionnaireId);
      } else {
        queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
        if (!isLoadingOnUpdate) {
          handleCategoryChange();
        }
      }
    },
    onError: err => showErrorToast(toast, err.message)
  };

  const {
    mutate: update,
    isLoading: isLoadingOnUpdate
  } = useUpdateWasteGeneratedInOperationsCategory(queryConfig);

  const formId = `formStep${tabKey}`;

  const onSubmit = (() => {
    if (!isPreview) {
      if (isDirty) {
        update({ questionnaireId, data: {} });
      } else {
        finalize ? finalizeQuestionnaire(questionnaireId) : handleCategoryChange();
      }
    } else {
      handleCategoryChange();
    }
  });

  const prepareSectionValues = useCallback(items => {
    const wasteActivityKey = "organizationQuestionnaire.scope3.inputs.wasteGeneratedInOperations.wasteActivity.options";
    const wasteTypeKey = "organizationQuestionnaire.scope3.inputs.wasteGeneratedInOperations.wasteType.options";
    const disposalMethodKey = "organizationQuestionnaire.scope3.inputs.wasteGeneratedInOperations.disposalMethod.options";

    return items.map(item => {
      return {
        id: item.id ?? null,
        dimensionItems: item.dimensionItems ?? null,
        dimensionOneItem: item.dimensionItems?.dimensionOneItem ?? null,
        dimensionTwoItem: item.dimensionItems?.dimensionTwoItem ?? null,
        dimensionThreeItem: item.dimensionItems?.dimensionThreeItem ?? null,
        dimensionFourItem: item.dimensionItems?.dimensionFourItem ?? null,
        dimensionFiveItem: item.dimensionItems?.dimensionFiveItem ?? null,
        wasteActivityValue: item.wasteActivity,
        wasteActivity: i18n.exists(`${wasteActivityKey}.${item.wasteActivity}`) ?
          t(`inputs.wasteGeneratedInOperations.wasteActivity.options.${item.wasteActivity}`) : item.wasteActivity ?? "",
        wasteTypeValue: item.wasteType,
        wasteType: i18n.exists(`${wasteTypeKey}.${item.wasteActivity}.${item.wasteType}`) ?
          t(`inputs.wasteGeneratedInOperations.wasteType.options.${item.wasteActivity}.${item.wasteType}`) : item.wasteType ?? "",
        disposalMethodValue: item.disposalMethod,
        disposalMethod: i18n.exists(`${disposalMethodKey}.${item.disposalMethod}`) ?
          t(`inputs.wasteGeneratedInOperations.disposalMethod.options.${item.disposalMethod}`) : item.disposalMethod ?? "",
        weight: item.weight ?? ""
      };
    });
  }, [i18n, t]);

  const handleAddItem = () => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      columnHeaders: [...colHeaders, ""],
      modalHeaderItemsType: t(`inputs.${FORM_INPUT_KEY}.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleEditItem = (item) => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      editData: {
        id: item.id,
        dimensionItems: item.dimensionItems ?? null,
        wasteActivity: item.wasteActivityValue,
        wasteType: item.wasteTypeValue,
        disposalMethod: item.disposalMethodValue,
        weight: item.weight
      },
      columnHeaders: colHeaders,
      modalHeaderItemsType: t(`inputs.${FORM_INPUT_KEY}.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleDeleteItem = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id });
  };

  const handleCategoryModalClose = () => {
    setCategoryModalData(null);
    onCategoryModalClose();
  };

  const { mutate: addItem } = useAddWasteGeneratedInOperationsItems({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(wasteGeneratedInOperationsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdateWasteGeneratedInOperationsItem({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(wasteGeneratedInOperationsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: deleteItem } = useDeleteWasteGeneratedInOperationsItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(wasteGeneratedInOperationsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: downloadTemplate } = useDownloadWasteGeneratedInOperationsTemplate({
    onMutate: () => dispatch(showAppSpinner()),
    onError: err => {
      const parsed = err.response?.data
        ? JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)))
        : null;
      showErrorToast(toast, parsed?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: importItems } = useImportWasteGeneratedInOperationsItems({
    onSuccess: () => {
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.imported"));
      queryClient.invalidateQueries(wasteGeneratedInOperationsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleImport = (e) => {
    if (!e.target.files?.[0]) return;
    const formData = new FormData();
    formData.append("file", e.target.files[0]);
    dispatch(showAppSpinner());
    importItems({ questionnaireId, data: formData });
  };

  const handleCategoryModalSubmit = (formData) => {
    const apiData = formData[categoryModalData.fieldArrayName].map(el => withDimensionItems({ ...el }));

    dispatch(showAppSpinner());
    if (categoryModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0]
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData
      });
    }
  };

  const getEmptyRow = () => {
    return {
      wasteActivity: "",
      wasteType: "",
      disposalMethod: "",
      weight: "",
      ...buildDimensionFormDefaults(organizationStructure, dimensionItems)
    };
  };

  return (
    <CategoryForm onSubmit={handleSubmit(onSubmit)} id={formId} marginTop={4}>
      <Box overflow="auto">
        <Accordion defaultIndex={[1]} allowMultiple marginBottom="10">
          {/* Wastewater */}
          <AccordionItem borderBottom="none">
            <AccordionButton px={0} pt={2} pb={4} _hover={{ bg: "transparent" }}>
              <Text as="h2" fontSize="heading.m" fontWeight="700" color="gray.600">
                {t(`inputs.wastewater`)}
              </Text>
              <AccordionIcon mr={0} ml="auto" boxSize={6} />
            </AccordionButton>
            <AccordionPanel px={0} overflow="auto">
              <WastewaterGrid
                questionnaireId={questionnaireId}
                organizationStructure={organizationStructure}
                dimensionItems={dimensionItems}
                isPreview={isPreview}
                tabKey={tabKey}
              />
            </AccordionPanel>
          </AccordionItem>

          {/* Waste Generated in Operations items */}
          <AccordionItem borderBottom="none">
            <AccordionButton px={0} pt={2} pb={4} _hover={{ bg: "transparent" }}>
              <Text as="h2" fontSize="heading.m" fontWeight="700" color="gray.600">
                Waste Generated in Operations
              </Text>
              <AccordionIcon mr={0} ml="auto" boxSize={6} />
            </AccordionButton>
            <AccordionPanel px={0} overflow="auto">
              <CategoryGrid
                questionnaireId={questionnaireId}
                columnHeaders={colHeaders}
                attributesOrder={attributesOrder}
                prepareSectionValues={prepareSectionValues}
                handleAddItem={handleAddItem}
                handleEditItem={handleEditItem}
                handleDeleteItem={handleDeleteItem}
                getItemsFunction={useGetWasteGeneratedInOperationsItems}
                isPreview={isPreview}
                organizationStructure={organizationStructure}
                onDownloadTemplate={() => downloadTemplate({ questionnaireId })}
                onImport={handleImport}
                importBtnId={`import-${tabKey}-btn`}
                totalsConfig={{ valueKey: "weight" }}
              />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Box>

      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />
      {
        isCategoryModalOpen && categoryModalData !== null && (
          <WasteGeneratedInOperationsModalWrapper
            tabKey={tabKey}
            modalData={categoryModalData}
            attributesOrder={attributesOrder}
            isOpen={isCategoryModalOpen}
            onClose={handleCategoryModalClose}
            onSubmit={handleCategoryModalSubmit}
            getEmptyRow={getEmptyRow}
          />
        )
      }
    </CategoryForm>
  );
}

export default WasteGeneratedInOperations;